import React, { useState } from 'react';
import { XIcon, DesktopIcon, TabletIcon, PhoneIcon, RocketIcon } from './Icons';
import { Template } from '../pages/TemplatesPage';


interface TemplatePreviewModalProps {
  template: Template;
  htmlContent: string;
  onClose: () => void;
  onUseTemplate: (prompt: string) => void;
}

type Device = 'desktop' | 'tablet' | 'mobile';

const deviceWidths: Record<Device, string> = {
  desktop: '100%',
  tablet: '768px',
  mobile: '375px',
};

const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({ template, htmlContent, onClose, onUseTemplate }) => {
  const [device, setDevice] = useState<Device>('desktop');

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex flex-col p-4 md:p-8 preview-modal-overlay" onClick={onClose}>
      <div 
        className="w-full h-full bg-[var(--bg-color)] border border-[var(--border-color)] rounded-xl flex flex-col preview-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-[var(--border-color)]">
          <h3 className="font-bold text-lg hidden md:block">{template.name}</h3>
          
          {/* Device Toggles */}
          <div className="flex items-center space-x-2 p-1 bg-black/20 rounded-full mx-auto">
            {(['desktop', 'tablet', 'mobile'] as Device[]).map(d => (
              <button 
                key={d} 
                onClick={() => setDevice(d)} 
                className={`p-2 rounded-full transition-colors ${device === d ? 'bg-gradient-to-r from-[var(--gradient-from)] to-[var(--gradient-to)] text-white' : 'text-[var(--text-secondary)] hover:bg-white/10'}`}
                title={`View on ${d.charAt(0).toUpperCase() + d.slice(1)}`}
              >
                {d === 'desktop' && <DesktopIcon />}
                {d === 'tablet' && <TabletIcon />}
                {d === 'mobile' && <PhoneIcon />}
              </button>
            ))}
          </div>

          <div className="flex items-center space-x-4">
            <button 
              onClick={() => onUseTemplate(template.prompt)}
              className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-[var(--gradient-from)] to-[var(--gradient-to)] rounded-md hover:opacity-90 transition-opacity flex items-center space-x-2"
            >
              <RocketIcon />
              <span>Use Template</span>
            </button>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 text-[var(--text-secondary)]">
              <XIcon />
            </button>
          </div>
        </div>

        {/* Iframe Content */}
        <div className="flex-grow p-4 bg-black/10 flex items-center justify-center overflow-auto">
          <iframe
            srcDoc={htmlContent}
            title={`Preview of ${template.name}`}
            className="bg-white rounded-md shadow-2xl transition-all duration-500 ease-in-out"
            style={{
              width: deviceWidths[device],
              height: '100%',
              maxWidth: '100%',
            }}
            sandbox="allow-scripts"
          />
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewModal;